import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { UserRole } from '@bhops/shared';
import { useAuthStore } from '@/stores/auth';

interface Props {
  children: ReactNode;
  /**
   * 단일 role 허용. 하위 호환을 위해 유지. `requiredRoles` 와 동시 지정 시
   * union 으로 합쳐서 허용.
   */
  requiredRole?: UserRole;
  /**
   * docs/work_log/p2-protected-route-multi-role — 백엔드가 ADMIN+MANAGER 등
   * 여러 role 을 허용하는 mutation 페이지(e.g. /suppliers, /parts/catalog,
   * /parts/lots) 가 늘어남에 따라 다중 role 게이트가 필요해졌음. 배열에 포함된
   * role 중 하나라도 일치하면 통과. ADMIN 은 항상 통과 (super-role).
   */
  requiredRoles?: ReadonlyArray<UserRole>;
}

export function ProtectedRoute({ children, requiredRole, requiredRoles }: Props) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!user) {
    // 쿼리까지 넘긴다 — 현장 앱의 '서명 만들기' 링크(?new=1&customerId=…)가 로그인을 거쳐도 살아남게.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // 허용 role 집합 — 두 prop 의 union. 빈 집합이면 모든 인증 사용자 허용.
  const allowed = new Set<UserRole>();
  if (requiredRole) allowed.add(requiredRole);
  if (requiredRoles) for (const r of requiredRoles) allowed.add(r);

  if (allowed.size > 0 && !allowed.has(user.role)) {
    // ADMIN 은 super-role: 모든 게이트 통과.
    if (user.role !== 'ADMIN') {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
